'use client';

import { Button } from '@ahafood/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@ahafood/ui/components/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@ahafood/ui/components/drawer';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { Facebook } from 'lucide-react';
import ImageWithFallback from '../../components/items/fallback-image';
import type { Item } from '@/api/use-get-menu-ws';

interface Store {
  _id: number;
  name: string;
  image_url: string;
  address: string;
  distance: number;
  page_id: number;
  ai_tags?: string[];
  nearest_branch?: {
    address: string;
  };
}

interface OrderDialogProps {
  showOrderDialog: boolean;
  selectedItem: Item | null;
  store: Store;
  isMobile: boolean;
  onClose: () => void;
  onSendMessage: (pageId: number) => void;
}

const getFinalPrice = (item: Item): number => {
  return item.discounted_price ?? item.price;
};

export function OrderDialog({
  showOrderDialog,
  selectedItem,
  store,
  isMobile,
  onClose,
  onSendMessage,
}: OrderDialogProps) {
  const handleSendMessage = () => {
    onSendMessage(store?.page_id ?? 0);
    onClose();
  };

  const ItemDisplay = () =>
    selectedItem && (
      <div className="flex items-center gap-4 p-4 bg-neutral-50 rounded-lg">
        <div className="size-16 bg-[#f2f4f8] rounded-md overflow-hidden shrink-0">
          <ImageWithFallback
            src={
              selectedItem.image_thumb_url &&
              selectedItem.image_thumb_url.trim() !== ''
                ? selectedItem.image_thumb_url
                : `${assetPrefix}/static/images/empty-store.webp`
            }
            alt={selectedItem.name}
            width={64}
            height={64}
            className="object-cover size-full"
            fallbackSrc={`${assetPrefix}/static/images/empty-store.webp`}
          />
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-[#1a1a1a] mb-1">
            {selectedItem.name}
          </h4>
          <div className="flex items-center gap-2">
            <span className="font-bold text-[#1a1a1a]">
              đ{getFinalPrice(selectedItem).toLocaleString()}
            </span>
            {selectedItem.discounted_price !== selectedItem.price && (
              <span className="text-sm text-[#b3b3b3] line-through">
                đ{selectedItem.price.toLocaleString()}
              </span>
            )}
          </div>
        </div>
      </div>
    );

  if (isMobile) {
    return (
      <Drawer open={showOrderDialog} onOpenChange={onClose}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>Đặt món qua Chat</DrawerTitle>
          </DrawerHeader>

          <div className="px-4">
            <ItemDisplay />
          </div>

          <DrawerFooter>
            <Button
              className="w-full bg-gradient-to-r from-yellow-40 to-primary-50 text-white font-bold"
              onClick={handleSendMessage}
            >
              <Facebook className="size-4 mr-2" />
              Chat để đặt đơn ngay
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={showOrderDialog} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Đặt món qua Chat</DialogTitle>
        </DialogHeader>

        <ItemDisplay />

        <div className="flex flex-col gap-3 mt-4">
          <Button
            className="w-full bg-gradient-to-r from-yellow-40 to-primary-50 text-white font-bold"
            onClick={handleSendMessage}
          >
            <Facebook className="size-4 mr-2" />
            Chat để đặt đơn ngay
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
